/**
 * Every user-facing string in one place.
 *
 * English only for the initial build (docs/04 §4.10), but centralised so a
 * translator can add a second language without touching component code.
 * Components must never hard-code display text.
 */

export const strings = {
  app: {
    name: "EKSUTH Antibiogram",
    tagline: "Isolate entry and quality assurance",
  },

  common: {
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    edit: "Edit",
    delete: "Delete",
    search: "Search",
    filter: "Filter",
    loading: "Loading…",
    retry: "Try again",
    back: "Back",
    next: "Next",
    previous: "Previous",
    yes: "Yes",
    no: "No",
    none: "None",
    required: "Required",
    optional: "optional",
    notTested: "Not tested",
    unknown: "Unknown",
    showMore: "Show more",
    signOut: "Sign out",
    help: "Help",
  },

  auth: {
    signIn: "Sign in",
    email: "Hospital email",
    password: "Password",
    forgotPassword: "Forgotten your password?",
    twoFactorPrompt: "Enter the 6-digit code from your authenticator app",
    twoFactorCode: "Authentication code",
    verify: "Verify",
    signingIn: "Signing in…",
    offlineNotice:
      "You are offline. Your saved session will keep working, but signing in for the first time needs a connection.",
    sessionExpired: "Your session has ended. Please sign in again.",
    confidentiality:
      "I have read and accept the confidentiality agreement. I will not share my account, and I will not copy patient data off this platform.",
    setPassword: "Choose your password",
    passwordHint: "At least 12 characters. A short phrase works well.",
  },

  sync: {
    online: "Online, all synced",
    offline: (queued: number) =>
      queued === 0
        ? "Offline"
        : `Offline, ${queued} waiting to sync`,
    syncing: (remaining: number) => `Syncing, ${remaining} remaining`,
    error: "Sync problem — tap to review",
    pendingBadge: "Waiting to sync",
    syncedBadge: "Saved to the server",
    conflictBadge: "Needs your attention",
    reviewQueue: "Review the sync queue",
    nothingPending: "Everything you have entered is saved on the server.",
    storageWarning:
      "This device is running out of space for offline records. Connect to the network so the queue can upload.",
    storageFull:
      "There is no room left to save offline. Connect to the network before entering more records, or nothing new can be stored.",
  },

  entry: {
    title: "New isolate",
    specimenCard: "Specimen",
    cultureCard: "Culture",
    organismCard: "Organism and sensitivities",
    labNumber: "Lab number",
    hospitalNumber: "Hospital number",
    age: "Age",
    ageUnit: "Unit",
    sex: "Sex",
    ward: "Ward",
    specimenDate: "Specimen date",
    specimenType: "Specimen type",
    cultureResult: "Culture result",
    organism: "Organism",
    organismOther: "Organism as written on the register",
    isolateNumber: "Isolate number",
    antibiotics: "Antibiotics",
    notes: "Notes",
    registerBook: "Register book",
    registerPage: "Register page",
    saveAndNew: "Save and new",
    saveAndNextOrganism: "Save and next organism (same specimen)",
    saveDraft: "Save as draft",
    noGrowthNotice:
      "No growth was recorded, so the organism and antibiotic sections are not needed.",
    shortcuts: "Keyboard shortcuts",
    whyThisRule: "Why?",
    savedToast: (recordId: string) => `Saved as ${recordId}`,
    savedOfflineToast: "Saved on this device. It will upload when you reconnect.",
  },

  qa: {
    blocking: "Must be fixed before saving",
    warning: "Check this",
    info: "For information",
    dismiss: "I have checked this",
    resolve: "Resolve",
    resolveReason: "What did you check?",
    resolveReasonHint:
      "A short note, recorded in the audit log against your name.",
    openFlags: "Open QA flags",
    noFlags: "No outstanding QA flags.",
    queueTitle: "QA flags",
  },

  master: {
    title: "All isolates",
    exportCsv: "Export BacLink CSV",
    columns: "Columns",
    noRows: "No isolates match these filters.",
    rowsShown: (shown: number, total: number | null) =>
      total === null ? `${shown} rows` : `${shown} of ${total} rows`,
    liveUpdates: "Updating live",
  },

  dashboard: {
    title: "Progress",
    totalIsolates: "Total isolates",
    complete: "Complete",
    needsReview: "Needs review",
    voided: "Void",
    openFlags: "Open QA flags",
    enteredToday: "Entered today",
    perDay: "Entries this week",
    byVolunteer: "By volunteer",
    staleNotice:
      "These numbers are from the last time you were online, so they may not be current.",
  },

  exportDialog: {
    title: "Export BacLink CSV",
    rowsIncluded: (n: number) => `${n} isolate${n === 1 ? "" : "s"} will be included`,
    blockedNotice: (n: number) =>
      `${n} isolate${n === 1 ? " is" : "s are"} held back by unresolved blocking flags.`,
    includeDrafts: "Include drafts",
    includeVoid: "Include void rows",
    generate: "Generate",
    download: "Download CSV",
    downloadMetadata: "Download metadata",
    generatedAt: "Generated",
    checksum: "SHA-256",
  },

  admin: {
    users: "Users",
    addUser: "Add user",
    deactivate: "Deactivate",
    reinvite: "Send a new signup link",
    inviteCreated:
      "Account created. Copy the signup link below and send it to them — valid for 7 days (1 week).",
    referenceData: "Reference data",
    changeNote: "Why are you making this change?",
    auditLog: "Audit log",
    systemHealth: "System health",
    sessions: "Active sessions",
    forceLogout: "Force sign-out",
    tbcNotice:
      "Awaiting microbiologist confirmation. Entries recorded against it will still export.",
  },

  errors: {
    generic: "Something went wrong. Please try again.",
    network: "Could not reach the server. Your work is saved on this device.",
    forbidden: "You do not have permission to do that.",
    notFound: "That record could not be found.",
    conflict: "This record changed while you were working on it.",
    validation: "Please check the highlighted fields.",
    rateLimited: "Too many attempts. Please wait and try again.",
    traceId: (id: string) => `Reference for IT: ${id}`,
  },
} as const;

export type Strings = typeof strings;
